import { useState } from "react";

interface CompositeIconProps {
  /** URL served by the API; a composite without one renders no icon at all. */
  icon: string | null;
  /** Sizes the icon, which differs per surface, so the wrapper class comes from the caller. */
  className?: string;
}

/**
 * Composite icons are remote assets, so a URL that stops resolving would leave the browser's
 * broken-image placeholder inside a report header or chart title. Dropping the element on a load
 * failure degrades to the same clean layout as a composite that has no icon.
 *
 * Keyed on the failed URL rather than a flag so a later icon change is still attempted.
 *
 * Deliberately not `loading="lazy"`: reports print through `window.print()` on the live DOM, where a
 * below-the-fold icon that has not been fetched yet is not guaranteed to appear in the output. The
 * icons are a few KB each, so deferring them would save nothing worth that risk.
 */
export function CompositeIcon({ icon, className }: CompositeIconProps) {
  const [failedIcon, setFailedIcon] = useState<string | null>(null);

  if (!icon || icon === failedIcon) return null;

  return (
    <div className={className}>
      <img src={icon} alt="" data-testid="composite-icon" decoding="async" onError={() => setFailedIcon(icon)} />
    </div>
  );
}
